import { reportMv, type ValLab } from './lx'

/** 可见 ≥ 该比例开始计时 */
export const DEFAULT_ENTER_RATIO = 0.5
/** 完全离开视口后再多滑出该距离，才算彻底离开 */
export const DEFAULT_LEAVE_BUFF_PX = 80

export interface ModuleStayTracker {
  start(key: string | number, extra?: ValLab): void
  end(key: string | number, extra?: ValLab): void
  endAll(): void
  isActive(key: string | number): boolean
}

export interface ModuleStayTarget {
  el: HTMLElement
  /** 实例唯一键（如卡片 index） */
  key: string | number
  /** 透传给 reportMv 的业务参数；也可函数惰性取 */
  extra?: ValLab | (() => ValLab)
}

export interface ObserveModuleStayOptions {
  bid: string
  enterRatio?: number
  leaveBuffPx?: number
  /**
   * 任意露出/离开时的回调（与停留起止无关）
   * 例如能力区用来切换 is-revealed 动效
   */
  onVisibilityChange?: (payload: {
    el: HTMLElement
    key: string
    visible: boolean
    ratio: number
  }) => void
}

export interface ModuleStayController {
  destroy(): void
}

function toKey(key: string | number): string {
  return String(key)
}

function resolveExtra(extra?: ValLab | (() => ValLab)): ValLab {
  if (typeof extra === 'function') return extra() || {}
  return extra || {}
}

function visibleRatio(el: HTMLElement): number {
  const rect = el.getBoundingClientRect()
  if (rect.height <= 0) return 0
  const visibleTop = Math.max(0, rect.top)
  const visibleBottom = Math.min(window.innerHeight, rect.bottom)
  return Math.max(0, visibleBottom - visibleTop) / rect.height
}

/**
 * 多实例停留计时 + reportMv（start/end）
 * extra 在 start 时缓存，end / endAll 自动带上，避免结算丢业务字段
 */
export function createModuleStayTracker(bid: string): ModuleStayTracker {
  const stayMap = new Map<string, { active: boolean; enterAt: number; extra: ValLab }>()

  const start: ModuleStayTracker['start'] = (key, extra = {}) => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
    const k = toKey(key)
    const state = stayMap.get(k) ?? { active: false, enterAt: 0, extra: {} }
    if (state.active) return
    state.active = true
    state.enterAt = Date.now()
    state.extra = { ...extra }
    stayMap.set(k, state)
    reportMv(bid, { module_type: 'start', ...state.extra })
  }

  const end: ModuleStayTracker['end'] = (key, extra = {}) => {
    const k = toKey(key)
    const state = stayMap.get(k)
    if (!state?.active) return
    const durationMs = Date.now() - state.enterAt
    const merged = { ...state.extra, ...extra }
    state.active = false
    state.enterAt = 0
    stayMap.set(k, state)
    reportMv(bid, { module_type: 'end', durationMs, ...merged })
  }

  const endAll: ModuleStayTracker['endAll'] = () => {
    stayMap.forEach((state, k) => {
      if (state.active) end(k)
    })
  }

  const isActive: ModuleStayTracker['isActive'] = (key) => {
    return Boolean(stayMap.get(toKey(key))?.active)
  }

  return { start, end, endAll, isActive }
}

/**
 * 通用模块停留观测：进入比例 / 离开 buffer / 切 Tab 结算 / MV 上报
 * 业务组件只需传入 targets + bid，不必自己写 Observer。
 *
 * @example
 * const stay = observeModuleStay(
 *   rows.map((el, i) => ({
 *     el,
 *     key: i,
 *     extra: { module_identifier: `${i + 1}` }
 *   })),
 *   { bid: 'b_xxx_mv' }
 * )
 * // unmount 时
 * stay.destroy()
 */
export function observeModuleStay(
  targets: ModuleStayTarget[],
  options: ObserveModuleStayOptions
): ModuleStayController {
  const {
    bid,
    enterRatio = DEFAULT_ENTER_RATIO,
    leaveBuffPx = DEFAULT_LEAVE_BUFF_PX,
    onVisibilityChange
  } = options

  const tracker = createModuleStayTracker(bid)
  const list = targets.filter((t) => t.el)

  const startByTarget = (target: ModuleStayTarget) => {
    tracker.start(target.key, resolveExtra(target.extra))
  }

  const endByTarget = (target: ModuleStayTarget) => {
    tracker.end(target.key, resolveExtra(target.extra))
  }

  const findTarget = (el: Element): ModuleStayTarget | undefined => {
    return list.find((t) => t.el === el)
  }

  const onDocumentVisibility = () => {
    if (document.visibilityState === 'hidden') {
      tracker.endAll()
      return
    }
    // 回到前台：仍 ≥ enterRatio 的从 0 重新计时
    list.forEach((target) => {
      if (visibleRatio(target.el) >= enterRatio) startByTarget(target)
    })
  }

  let enterObserver: IntersectionObserver | undefined
  let leaveObserver: IntersectionObserver | undefined

  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    list.forEach(startByTarget)
    document.addEventListener('visibilitychange', onDocumentVisibility)
    return {
      destroy() {
        tracker.endAll()
        document.removeEventListener('visibilitychange', onDocumentVisibility)
      }
    }
  }

  enterObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const target = findTarget(entry.target)
        if (!target) return
        onVisibilityChange?.({
          el: target.el,
          key: toKey(target.key),
          visible: entry.intersectionRatio > 0,
          ratio: entry.intersectionRatio
        })
        if (entry.intersectionRatio >= enterRatio) startByTarget(target)
      })
    },
    { threshold: [0, 0.25, 0.5, 0.75, 1] }
  )

  leaveObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const target = findTarget(entry.target)
        if (!target) return
        if (!entry.isIntersecting) endByTarget(target)
      })
    },
    {
      root: null,
      rootMargin: `${leaveBuffPx}px 0px ${leaveBuffPx}px 0px`,
      threshold: 0
    }
  )

  list.forEach((target) => {
    enterObserver?.observe(target.el)
    leaveObserver?.observe(target.el)
  })
  document.addEventListener('visibilitychange', onDocumentVisibility)

  return {
    destroy() {
      tracker.endAll()
      enterObserver?.disconnect()
      leaveObserver?.disconnect()
      document.removeEventListener('visibilitychange', onDocumentVisibility)
    }
  }
}

export interface DomMvTarget extends ModuleStayTarget {
  bid: string
}

/** data-mv-module-identifier → module_identifier */
function mvDatasetKeyToSnake(datasetKey: string): string {
  // mvModuleIdentifier → ModuleIdentifier → module_identifier
  const camel = datasetKey.slice(2)
  return camel.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')
}

/**
 * 读取节点上的业务参数（每次调用读最新 DOM，便于 live_steps 等动态字段）
 * - data-mv-extra：JSON
 * - 其它 data-mv-*（除 bid/key/extra）：转成 snake_case 写入 extra
 */
export function parseMvExtraFromEl(el: HTMLElement): ValLab {
  const extra: ValLab = {}
  const rawExtra = el.dataset.mvExtra
  if (rawExtra) {
    try {
      Object.assign(extra, JSON.parse(rawExtra) as ValLab)
    } catch (error) {
      console.warn('[moduleStay] data-mv-extra JSON 解析失败：', rawExtra, error)
    }
  }

  Object.entries(el.dataset).forEach(([key, value]) => {
    if (value == null) return
    if (key === 'mvBid' || key === 'mvKey' || key === 'mvExtra') return
    if (!key.startsWith('mv') || key.length <= 2) return
    extra[mvDatasetKeyToSnake(key)] = value
  })

  return extra
}

/**
 * 从 DOM 收集 [data-mv-bid] 节点
 *
 * 支持属性：
 * - data-mv-bid（必填）
 * - data-mv-key（可选，默认 bid#序号）
 * - data-mv-extra（可选，JSON）
 * - data-mv-* 业务字段（如 module-identifier / live-steps / button-name）
 * MV 上报时自动带 type；结束时自动带 duration（分钟）
 */
export function collectMvStayTargets(root: ParentNode = document): DomMvTarget[] {
  const nodes = Array.from(root.querySelectorAll<HTMLElement>('[data-mv-bid]'))
  const targets: DomMvTarget[] = []

  nodes.forEach((el, index) => {
    const bid = el.dataset.mvBid?.trim()
    if (!bid) return

    targets.push({
      el,
      bid,
      key: el.dataset.mvKey || `${bid}#${index}`,
      // 惰性读取，保证切换步骤等场景 end 时拿到最新参数
      extra: () => parseMvExtraFromEl(el)
    })
  })

  return targets
}

/**
 * App 层统一绑定：扫描 data-mv-bid，按 bid 分组观测。
 * 单页落地页在 onMounted 调一次即可；页面卸载时 destroy。
 */
export function bindMvStayFromDom(
  root: ParentNode = document,
  options: Omit<ObserveModuleStayOptions, 'bid'> = {}
): ModuleStayController {
  const targets = collectMvStayTargets(root)
  const grouped = new Map<string, ModuleStayTarget[]>()

  targets.forEach((target) => {
    const list = grouped.get(target.bid) || []
    list.push({ el: target.el, key: target.key, extra: target.extra })
    grouped.set(target.bid, list)
  })

  const controllers = Array.from(grouped.entries()).map(([bid, list]) =>
    observeModuleStay(list, { ...options, bid })
  )

  return {
    destroy() {
      controllers.forEach((controller) => controller.destroy())
    }
  }
}
